// CONTRACT:C2-RENDER-ENGINE.1.0
/** CALLSIGN DECLUTTER — a fluid, Y-ONLY anti-collision layout so map labels never pile
 *  into an illegible smear (render-polish: "callsign declutter").
 *
 *  WHAT: Bundle A draws each track's callsign as a deck.gl TextLayer beneath its dot
 *  (apps/cop/src/host/deckBackend.ts buildCallsigns). In a dense cluster the labels
 *  collide illegibly. This is the pure layout math that deconflicts them: it keeps every
 *  label's X anchored under its dot (X is NEVER moved) and resolves collisions by nudging
 *  the Y offset only, then SHRINKS a label that would otherwise have to drift too far.
 *
 *  THE THREE RULES (all here, all tested):
 *   1. Y-ONLY. The output is {yOffset, size} per label; X is never an output — the caller
 *      keeps each label centred under its dot. A horizontal-overlap gate means labels whose
 *      X-boxes don't overlap never push each other (columns deconflict independently).
 *   2. GRADIENT, NOT SNAP. A small vertical overlap is tolerated: up to ~½ character-height
 *      of overlap gets NO push. As the overlap grows from ½ char toward 3 chars, the push
 *      ramps LINEARLY across that band (zero at <={@link OVERLAP_TOL_CHARS} char, full
 *      overlap-removal at >=3 char). The response (see {@link overlapPush}) is continuous in
 *      the overlap — no hard snap. Iterated to convergence, a column settles to a calm
 *      uniform ~½-char tolerance gap (≈2px residual max, plate-inclusive — v2 §1.3).
 *   3. SHRINK FALLBACK. If a label would have to drift more than {@link DRIFT_LIMIT_MULT}x
 *      its own height from its anchor, it SHRINKS (font/box smaller) so the pile packs
 *      tighter and its drift falls back within the limit — a dense pile auto-shrinks instead
 *      of stretching into an unbounded vertical ribbon.
 *
 *  PLATE-INCLUSIVE BOXES (DESIGN-CLUTTER-PRIORITY v2 §1.3 — collision honesty). The COP draws
 *  a protective PLATE behind every label (deck backgroundPadding), so the drawn box is the
 *  glyph box + the plate padding on every side. The collision math must see that TRUE box —
 *  when it saw only the glyphs, ~8px of plate-over-text survived "convergence" (pad excluded
 *  from both boxes + the old whole-char tolerance) and right-over-left paint order finished
 *  the smear. Callers pass the pad ({@link PlatePad}) into deconflictLabels /
 *  fastDeconflictLabels; the default [0, 0] is the bare glyph box (plates off).
 *
 *  "CHARACTER HEIGHT" CALIBRATION. One character's vertical height is 1/3 of the GLYPH line
 *  box ({@link CHAR_H_FRAC} — a glyph metric, never plate-padded), so the [½-char, 3-char]
 *  band spans [1/6 line, 1 full line] of overlap — i.e. tolerate ~a sixth of a line, ramp to
 *  full-removal as the overlap approaches a whole line.
 *
 *  VISIBILITY LAW (I13): presentation ONLY. Re-placing / shrinking a label NEVER changes WHAT
 *  the projection renders — the same labels go in as come out (every label is still drawn and
 *  its track still pickable); only yOffset/size change. It cannot reveal or hide a track.
 *
 *  PURE: imports nothing, deterministic (same input -> same output), no GPU. Tested in
 *  packages/render-engine/test/labelDeconflict.test.ts. */

/** One label to place. The caller supplies SCREEN-space anchors (px). */
export interface LabelInput {
  /** Anchor screen-X (the label centre, under its dot). FIXED — never moved. */
  x: number;
  /** Anchor screen-Y at zero deconfliction offset (the label's baseline placement, px). */
  y: number;
  /** Glyph count (drives the box width = chars * size * CHAR_ASPECT). */
  chars: number;
  /** Base font size (px) before any shrink. */
  size: number;
  /** PRIORITY label — exempt from the SHRINK fallback so it stays at its full input size and never
   *  drops below legibility. The COP marks all FRIENDLIES priority (operators must always be able to
   *  read friendly callsigns); a priority label still Y-nudges to deconflict, but neighbouring
   *  NON-priority labels shrink to pack around it instead of the priority label shrinking. */
  priority?: boolean;
  /** PLACEMENT RANK (lower = placed FIRST) for the FAST grid fallback: the COP passes
   *  globalRank (emphasized 0 → alert 1 → own force 2 → hostile 3 → unknown/pending 4 →
   *  neutral 5 — globalRank.ts), so the operationally-important names claim their slots — and
   *  survive the cap — before the noise. Ignored by the exact path. Default 0 (input order). */
  rank?: number;
}

/** Per-pass box padding [x, y] px added on EVERY side of a label's glyph box — the COP's
 *  protective plate (deck TextLayer backgroundPadding). Collision boxes must include it or
 *  plate-over-text survives "convergence" (v2 §1.3). [0, 0] = bare glyph box (plates off). */
export type PlatePad = readonly [number, number];
const NO_PAD: PlatePad = [0, 0];

/** The deconflicted placement for one label (same order as the input). */
export interface LabelPlacement {
  /** Extra vertical offset (px, downward +) to ADD to the anchor Y. X is unchanged. */
  yOffset: number;
  /** Resolved font size (px): == input size, or smaller if the label was shrunk to pack. */
  size: number;
  /** FAST path only: this label was DROPPED (its dense-cluster slot was too far from its anchor,
   *  or it fell beyond the name cap) — do NOT draw it. The exact path never sets this (all drawn). */
  hidden?: boolean;
}

/** Monospace glyph advance as a fraction of the em (box width = chars * size * this). */
export const CHAR_ASPECT = 0.62;
/** Line-box height as a fraction of the em (box height = size * this). */
export const LINE_FRAC = 1.15;
/** One character's vertical height as a fraction of the GLYPH line box (a glyph metric —
 *  never plate-padded); see header calibration. Band: tolerate <= OVERLAP_TOL_CHARS*charH
 *  overlap; ramp to full removal by 3*charH (== one line). */
export const CHAR_H_FRAC = 1 / 3;
/** Tolerance band START (in charH units): overlap at/below this many chars gets NO push.
 *  0.5 (was 1 — v2 §1.3): with plate-inclusive boxes the residual at-rest overlap is now
 *  ≈2px max, instead of the ~8px of plate-over-text the old whole-char band let survive. */
export const OVERLAP_TOL_CHARS = 0.5;
/** Drift past this multiple of a label's OWN height triggers the shrink fallback. */
export const DRIFT_LIMIT_MULT = 3;
/** Each shrink step multiplies the font size by this (smaller -> tighter pile). */
export const SHRINK_STEP = 0.82;
/** A label never shrinks below this many px (legibility floor). */
export const MIN_SIZE = 6;

const MAX_RELAX_ITERS = 40;
const MAX_OUTER_ITERS = 8;
const EPS = 1e-6;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** THE GRADIENT RESPONSE. Given a pair's current vertical box overlap `ov` (px) and the
 *  per-pair character height `charH` (px), return how much to SEPARATE the pair this step.
 *   - ov <= OVERLAP_TOL_CHARS*charH -> 0   (tolerated: the calm "~½ char overlap is fine" band)
 *   - ov >= 3*charH                 -> ov  (full removal: push the boxes completely apart)
 *   - between        -> k*ov, k = (ov - tol)/(full - tol) linearly ramped in [0,1]
 *  Continuous in `ov` (k is continuous and ov is continuous) so there is NO snap. Exported so
 *  the gradient is unit-tested directly across the [½-char, 3-char] band. */
export function overlapPush(ov: number, charH: number): number {
  if (!(ov > 0) || !(charH > 0)) return 0;
  const tol = OVERLAP_TOL_CHARS * charH;
  const full = 3 * charH;
  if (full <= tol) return ov; // degenerate guard
  const k = clamp01((ov - tol) / (full - tol));
  return k * ov;
}

/** A label's DRAWN box height (px): glyph line + the plate pad above AND below (v2 §1.3). */
function heightOf(size: number, padY: number): number {
  return size * LINE_FRAC + 2 * padY;
}
/** A label's DRAWN box width (px): glyphs + the plate pad left AND right (v2 §1.3). */
function widthOf(chars: number, size: number, padX: number): number {
  return Math.max(0, chars) * size * CHAR_ASPECT + 2 * padX;
}

/** Do two labels' X-boxes (plate-inclusive) overlap — the only case in which they can
 *  collide vertically? */
function xOverlap(
  ax: number,
  achars: number,
  asize: number,
  bx: number,
  bchars: number,
  bsize: number,
  padX: number,
): boolean {
  const halfA = widthOf(achars, asize, padX) / 2;
  const halfB = widthOf(bchars, bsize, padX) / 2;
  return Math.abs(ax - bx) < halfA + halfB;
}

/** Relax the per-label yOffset to convergence for the CURRENT sizes (mutates `yoff`).
 *  A deterministic Gauss-Seidel sweep over pairs, ordered by current centre Y so the layout
 *  is reproducible. Each overlapping, horizontally-colliding pair (PLATE-inclusive boxes) is
 *  pushed apart by {@link overlapPush}; the higher label moves up, the lower moves down
 *  (Y only). The tolerance charH stays a GLYPH metric (pad-free) — the pad grows the boxes,
 *  never the tolerance. */
function relax(
  inputs: readonly LabelInput[],
  sizes: number[],
  yoff: number[],
  pad: PlatePad,
): void {
  const n = inputs.length;
  const idx = inputs.map((_v, i) => i);
  for (let iter = 0; iter < MAX_RELAX_ITERS; iter++) {
    // Stable order: by current centre Y, tie-break X then original index (determinism).
    idx.sort((a, b) => {
      const ca = inputs[a].y + yoff[a];
      const cb = inputs[b].y + yoff[b];
      if (ca !== cb) return ca - cb;
      if (inputs[a].x !== inputs[b].x) return inputs[a].x - inputs[b].x;
      return a - b;
    });
    let moved = false;
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const i = idx[a];
        const j = idx[b];
        if (
          !xOverlap(
            inputs[i].x, inputs[i].chars, sizes[i],
            inputs[j].x, inputs[j].chars, sizes[j],
            pad[0],
          )
        ) {
          continue;
        }
        const hi = heightOf(sizes[i], pad[1]);
        const hj = heightOf(sizes[j], pad[1]);
        const ci = inputs[i].y + yoff[i];
        const cj = inputs[j].y + yoff[j];
        const ov = (hi + hj) / 2 - Math.abs(ci - cj);
        if (ov <= 0) continue;
        // charH from the GLYPH line boxes (pad-free): the tolerance is a text metric.
        const charH = ((sizes[i] + sizes[j]) / 2) * LINE_FRAC * CHAR_H_FRAC;
        const push = overlapPush(ov, charH);
        if (push <= EPS) continue;
        const half = push / 2;
        if (ci <= cj) {
          yoff[i] -= half;
          yoff[j] += half;
        } else {
          yoff[i] += half;
          yoff[j] -= half;
        }
        moved = true;
      }
    }
    if (!moved) break;
  }
}

/** Deconflict a set of VISIBLE labels into {yOffset, size} placements (same order in).
 *  Y-ONLY layout + shrink-fallback; PURE + deterministic. `opts.platePad` is the protective
 *  plate's per-side padding — pass it so the collision boxes match what deck actually draws
 *  (v2 §1.3); omitted = bare glyph boxes. See the module header. */
export function deconflictLabels(
  inputs: readonly LabelInput[],
  opts: { platePad?: PlatePad } = {},
): LabelPlacement[] {
  const pad = opts.platePad ?? NO_PAD;
  const n = inputs.length;
  const sizes = inputs.map((l) => (Number.isFinite(l.size) && l.size > 0 ? l.size : MIN_SIZE));
  const yoff = new Array<number>(n).fill(0);
  if (n <= 1) return inputs.map((_v, i) => ({ yOffset: 0, size: sizes[i] }));

  for (let outer = 0; outer < MAX_OUTER_ITERS; outer++) {
    // Re-relax from scratch with the current sizes (deterministic; no stale offsets).
    yoff.fill(0);
    relax(inputs, sizes, yoff, pad);
    // SHRINK FALLBACK: a label drifting past DRIFT_LIMIT_MULT x its own height must shrink.
    // Mechanism: smaller labels pack tighter (narrower boxes de-collide neighbouring COLUMNS,
    // shorter boxes compress the column). Because a perfectly-coincident pile keeps the
    // drift/limit RATIO constant under a uniform shrink, we compress the WHOLE colliding
    // column (every label that x-overlaps an over-limit one), not just the exceeding label —
    // so a dense pile auto-shrinks toward the legibility floor (MIN_SIZE) instead of stretching
    // into an unbounded ribbon. Distinct-x piles additionally de-collide as widths narrow.
    const overLimit: boolean[] = new Array(n).fill(false);
    for (let i = 0; i < n; i++) {
      if (Math.abs(yoff[i]) > DRIFT_LIMIT_MULT * heightOf(sizes[i], pad[1]) + EPS) overLimit[i] = true;
    }
    let shrank = false;
    for (let i = 0; i < n; i++) {
      if (sizes[i] <= MIN_SIZE) continue;
      // PRIORITY (friendly) labels are NEVER shrunk — they hold full legibility; the colliding
      // NON-priority labels in the column shrink to pack around them instead.
      if (inputs[i].priority) continue;
      // shrink i if it exceeds, OR if it shares a column with an exceeding label.
      let inColumn = overLimit[i];
      if (!inColumn) {
        for (let j = 0; j < n; j++) {
          if (!overLimit[j] || j === i) continue;
          if (
            xOverlap(
              inputs[i].x, inputs[i].chars, sizes[i],
              inputs[j].x, inputs[j].chars, sizes[j],
              pad[0],
            )
          ) {
            inColumn = true;
            break;
          }
        }
      }
      if (inColumn) {
        sizes[i] = Math.max(MIN_SIZE, sizes[i] * SHRINK_STEP);
        shrank = true;
      }
    }
    if (!shrank) break;
  }

  return inputs.map((_v, i) => ({ yOffset: yoff[i], size: sizes[i] }));
}

/** Tuning for the FAST grid deconfliction — the O(N) fallback for a DENSE label set. */
export const FAST_DECONFLICT = {
  /** Protect ~this many leading glyphs when reserving a label's column width (rough justice). */
  protectChars: 10,
  /** DROP a label whose nearest free row is farther than this many rows from its anchor — in that
   *  density a nudged name detaches from its dot and reads as noise (you zoom in for detail). */
  maxOffsetRows: 10,
  /** Hard CAP on names placed (highest-priority survive). Beyond this, names are meaningless at
   *  density — show the priority set (emphasized, alerts, own-force, threats), drop the rest. */
  cap: 1000,
} as const;

/** FAST grid/column deconfliction — O(N) graceful degradation for a dense label set, the fallback
 *  above the exact path's practical density (the exact pass is near-O(N²); bench: ~294ms@1k labels).
 *  Each label claims a SLOT in a uniform grid (column ≈ the protected box width × a row ≈ the
 *  plate-inclusive line height) at or near its anchor; a collision takes the NEAREST free row (up
 *  or down). Three density guards:
 *    (B) PRIORITY ORDER — placed by ascending `rank` (globalRank: emphasized/alerts/own-force
 *        first, neutral last), so important names claim slots — and survive the cap — before the
 *        noise; ties broken by index (stable).
 *    (C) CAP — at most `cap` names are placed; the rest are hidden.
 *    (A) DROP-AT-DISTANCE — a label whose nearest free row is > `maxOffsetRows` from its anchor is
 *        hidden (a name that has drifted that far is detached + meaningless at that density).
 *  Returns one placement per input (SAME order); a dropped/over-cap label has `hidden: true`. NO
 *  shrink (rough justice — the point is to SEE a cluster exists, then zoom in). PURE + deterministic
 *  (placement order = rank then index; the slot search is fixed nearest-first). */
export function fastDeconflictLabels(
  inputs: readonly LabelInput[],
  opts: { protectChars?: number; maxOffsetRows?: number; cap?: number; platePad?: PlatePad } = {},
): LabelPlacement[] {
  const n = inputs.length;
  const out: LabelPlacement[] = inputs.map((l) => ({
    yOffset: 0,
    size: Number.isFinite(l.size) && l.size > 0 ? l.size : MIN_SIZE,
    hidden: true, // default HIDDEN; cleared only when a slot is successfully claimed
  }));
  if (n === 0) return out;
  const protectChars = opts.protectChars ?? FAST_DECONFLICT.protectChars;
  const maxRows = opts.maxOffsetRows ?? FAST_DECONFLICT.maxOffsetRows;
  const cap = opts.cap ?? FAST_DECONFLICT.cap;
  const pad = opts.platePad ?? NO_PAD;
  // A representative size for the uniform grid (labels are near-uniform at the zoom this runs
  // at); cells are PLATE-inclusive so slot rows never draw plate-over-plate (v2 §1.3).
  const size0 = out[0].size;
  const cellW = Math.max(1, widthOf(protectChars, size0, pad[0]));
  const cellH = Math.max(1, heightOf(size0, pad[1]));
  // (B) placement order: rank ascending, then input index for determinism. Default rank 0.
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
    const ra = inputs[a].rank ?? 0;
    const rb = inputs[b].rank ?? 0;
    return ra !== rb ? ra - rb : a - b;
  });
  const occupied = new Map<number, Set<number>>(); // column → set of taken row indices
  let placed = 0;
  for (const i of order) {
    if (placed >= cap) break; // (C) cap reached — everyone after stays hidden (default)
    const l = inputs[i];
    if (!Number.isFinite(l.x) || !Number.isFinite(l.y)) continue; // unplaceable → hidden
    const col = Math.floor(l.x / cellW);
    const anchorRow = Math.round(l.y / cellH);
    let rows = occupied.get(col);
    if (!rows) occupied.set(col, (rows = new Set<number>()));
    // (A) nearest free row from the anchor outward (0, +1, −1, +2, −2, …), bounded by maxRows.
    let chosen: number | null = null;
    for (let d = 0; d <= maxRows; d++) {
      if (!rows.has(anchorRow + d)) {
        chosen = anchorRow + d;
        break;
      }
      if (d > 0 && !rows.has(anchorRow - d)) {
        chosen = anchorRow - d;
        break;
      }
    }
    if (chosen === null) continue; // (A) no free row within maxRows → hidden (too dense here)
    rows.add(chosen);
    out[i].yOffset = chosen * cellH - l.y;
    out[i].hidden = false;
    placed++;
  }
  return out;
}

// ============================================================================
//  CLUSTER LATCH — "a knot of labels stops fighting and stacks" (legibility
//  direction 2026-07-05: text gets hard to read when labels pile over a
//  saturated icon group even after the nudge/shrink passes; past a threshold
//  the honest layout is ONE left-justified column BESIDE the group, not N
//  jittering per-dot placements). Pure screen-space math, no GPU, no envelope
//  types — the COP host (deckBackend deconflictCallsigns) projects, density-
//  culls, then hands the SURVIVING anchors here. VISIBILITY LAW (I13) holds:
//  the same labels go in as come out — only their pixel placement changes.
// ============================================================================

/** One surviving label's anchor for the CLUSTER-LATCH pass. Coordinates are the MARKER's
 *  screen position (px) — the dot, not the label box. */
export interface ClusterAnchor {
  /** Stable entity id — the output key and the final deterministic tie-break. */
  id: string;
  /** Marker screen-X (px). */
  x: number;
  /** Marker screen-Y (px). */
  y: number;
  /** Glyph count of the label text (widest-row estimate for the viewport-edge flip). */
  chars: number;
  /** Stack-order rank (lower stacks first — the COP passes globalRank: emphasized 0, alert 1,
   *  friend 2, hostile 3, unknown/pending 4, neutral 5; globalRank.ts). */
  rank: number;
  /** The label text — the secondary stack-order key after rank (callsign-alphabetical). */
  text: string;
}

/** A latched label's resolved placement: the screen position its anchor point moves to plus
 *  which text anchor justifies it against the SHARED stack edge. 'start' = the normal column
 *  hanging RIGHT of the cluster (left-justified text); 'end' = the right-viewport-edge flip
 *  (column hangs LEFT of the cluster, right-justified against the shared edge). A latched
 *  label always renders at the FULL base size — the latch IS the declutter. */
export interface LatchedLabel {
  id: string;
  /** Target anchor screen-X (px) — the shared stack edge (identical for every member). */
  x: number;
  /** Target anchor screen-Y (px) — the member's row TOP (render with alignment 'top'). */
  y: number;
  anchor: "start" | "end";
  size: number;
}

/** Options for the latch pass (all px; the COP feeds these from render tuning). */
export interface LatchOptions {
  /** Single-linkage grouping radius: anchors within this distance union into one cluster. */
  radiusPx: number;
  /** Minimum group size that latches. <2 disables the whole pass (0 = the documented "off"). */
  latchAt: number;
  /** Full base font size — latched labels never shrink. */
  sizePx: number;
  /** Row pitch of the stacked column (≈ sizePx + 3). */
  lineHeightPx: number;
  /** Gap between the group's edge marker and the shared stack edge. */
  marginPx: number;
  /** Viewport width for the right-edge flip; Infinity = unknown (never flip — fail-to-default). */
  viewportW: number;
  /** Row CAP per latched column (Will 2026-07-05: "cap the number of items in the list to 20").
   *  A column longer than this renders its first maxRows members + ONE ellipsis row ("…") as
   *  the zoom-in cue; the remaining members are HIDDEN (their tracks still render — only labels
   *  drop). <=0 or non-finite = unlimited. */
  maxRows?: number;
}

/** The latch pass result. `placed` = the stacked member rows; `ellipses` = one "…" row per
 *  TRUNCATED column (its id is the first OVERFLOW member — the caller renders that entity's
 *  label as a literal "…" at this placement, a cue to zoom in); `hiddenIds` = the remaining
 *  overflow members (no label this frame — excluded from every other placement pass). */
export interface LatchResult {
  placed: LatchedLabel[];
  ellipses: LatchedLabel[];
  hiddenIds: string[];
}

/** Group anchors into proximity clusters: UNION-FIND over "distance ≤ radiusPx" pairs,
 *  grid-bucketed at radius-sized cells so only the 3×3 neighbourhood is compared (O(N)
 *  expected vs O(N²) all-pairs). SINGLE-LINKAGE by construction (A~B and B~C chain into one
 *  group even when A—C exceeds the radius) — a marching column reads as one cluster.
 *  DETERMINISTIC for a given input order: the union root is always the smallest member index,
 *  groups are emitted in ascending-root order, members in input order — so the caller's
 *  rank/id-sorted input (the stable-placement discipline) yields identical groups every
 *  rebuild. A non-finite anchor never unions (its own singleton). Returns index groups. */
export function clusterByProximity(
  anchors: readonly ClusterAnchor[],
  radiusPx: number,
): number[][] {
  const n = anchors.length;
  if (n === 0) return [];
  if (!(radiusPx > 0)) return anchors.map((_a, i) => [i]); // degenerate radius ⇒ all singletons
  const parent = Array.from({ length: n }, (_v, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]]; // path halving
      i = parent[i];
    }
    return i;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb); // root = smallest index (determinism)
  };
  const cell = radiusPx;
  const r2 = radiusPx * radiusPx;
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < n; i++) {
    const a = anchors[i];
    if (!Number.isFinite(a.x) || !Number.isFinite(a.y)) continue; // unplaceable ⇒ stays a singleton
    const cx = Math.floor(a.x / cell);
    const cy = Math.floor(a.y / cell);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const near = buckets.get(`${cx + dx}|${cy + dy}`);
        if (!near) continue;
        for (const j of near) {
          const b = anchors[j];
          const ddx = a.x - b.x;
          const ddy = a.y - b.y;
          if (ddx * ddx + ddy * ddy <= r2) union(i, j);
        }
      }
    }
    const key = `${cx}|${cy}`;
    let mine = buckets.get(key);
    if (!mine) buckets.set(key, (mine = []));
    mine.push(i);
  }
  const byRoot = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    let g = byRoot.get(r);
    if (!g) byRoot.set(r, (g = []));
    g.push(i);
  }
  return [...byRoot.entries()].sort((a, b) => a[0] - b[0]).map(([, g]) => g);
}

/** Place ONE latched group as a single stacked column. Geometry:
 *   - stack order: rank ascending (globalRank — emphasized/alerts crown, then friendly,
 *     then threats, neutral last), then label text, then id — fully deterministic so the
 *     ~1 Hz rebuild never reshuffles the column;
 *   - shared LEFT edge just RIGHT of the group's rightmost marker (+ marginPx), every member
 *     left-justified against it (anchor 'start');
 *   - RIGHT-EDGE FLIP: if the widest row would run past viewportW, the column hangs off the
 *     group's LEFT side instead (edge = leftmost marker − marginPx, anchor 'end') — the stack
 *     never runs off-screen; an unknown viewport (Infinity) never flips;
 *   - rows vertically CENTRED on the group's Y centroid at lineHeightPx pitch; each row's y is
 *     its TOP (callers render with alignmentBaseline 'top');
 *   - every member at the FULL base size (no shrink — the latch is the declutter). */
export function latchStack(
  members: readonly ClusterAnchor[],
  opts: Omit<LatchOptions, "radiusPx" | "latchAt">,
): LatchResult {
  const n = members.length;
  if (n === 0) return { placed: [], ellipses: [], hiddenIds: [] };
  const order = [...members].sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.text !== b.text) return a.text < b.text ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  let minX = Infinity;
  let maxX = -Infinity;
  let sumY = 0;
  let maxChars = 0;
  for (const m of members) {
    if (m.x < minX) minX = m.x;
    if (m.x > maxX) maxX = m.x;
    sumY += m.y;
    if (m.chars > maxChars) maxChars = m.chars;
  }
  const widest = maxChars * opts.sizePx * CHAR_ASPECT;
  // EDGE FIT — pick the side that actually has room, checking BOTH edges (not just the right).
  // Right stack extends +widest from (maxX+margin); left stack extends −widest from (minX−margin).
  // Prefer the RIGHT side (the canonical left-justified column); flip LEFT only when the right
  // overflows AND the left has room; if NEITHER fits (a knot wider than the viewport), keep the
  // side with more room so the column never lands fully offscreen. Unknown viewportW (Infinity)
  // ⇒ rightRoom is Infinity ⇒ never flips (fail-to-default: the normal right-side stack).
  const rightRoom = opts.viewportW - (maxX + opts.marginPx); // px of clear space to the right
  const leftRoom = minX - opts.marginPx; // px of clear space to the left (screen x=0 origin)
  const flip = rightRoom < widest && (leftRoom >= widest || leftRoom > rightRoom);
  const edgeX = flip ? minX - opts.marginPx : maxX + opts.marginPx;
  const anchor: "start" | "end" = flip ? "end" : "start";
  // ROW CAP: rows beyond maxRows collapse into one "…" cue row (the first overflow member
  // carries it); the rest hide. Centering counts the RENDERED rows (members + the cue row) so
  // a truncated column still balances on the group's Y centroid.
  const cap =
    typeof opts.maxRows === "number" && Number.isFinite(opts.maxRows) && opts.maxRows > 0
      ? Math.floor(opts.maxRows)
      : n;
  const rows = Math.min(n, cap);
  const truncated = n > rows;
  const renderRows = rows + (truncated ? 1 : 0);
  const yTop = sumY / n - (renderRows * opts.lineHeightPx) / 2;
  const at = (i: number): { x: number; y: number; anchor: "start" | "end"; size: number } => ({
    x: edgeX,
    y: yTop + i * opts.lineHeightPx,
    anchor,
    size: opts.sizePx,
  });
  return {
    placed: order.slice(0, rows).map((m, i) => ({ id: m.id, ...at(i) })),
    ellipses: truncated ? [{ id: order[rows].id, ...at(rows) }] : [],
    hiddenIds: truncated ? order.slice(rows + 1).map((m) => m.id) : [],
  };
}

/** The full CLUSTER-LATCH pass: group the surviving anchors by proximity, latch every group of
 *  ≥ latchAt members into a stacked column (latchStack), leave smaller groups alone. Returns
 *  the latched placements ONLY — the caller excludes those ids from the nudge/shrink passes and
 *  lets everything else flow through the existing pipeline unchanged. Disabled (empty result)
 *  when latchAt < 2 or the radius is degenerate. PURE + deterministic. */
export function latchClusters(
  anchors: readonly ClusterAnchor[],
  opts: LatchOptions,
): LatchResult {
  const out: LatchResult = { placed: [], ellipses: [], hiddenIds: [] };
  if (!(opts.latchAt >= 2) || !(opts.radiusPx > 0) || anchors.length < opts.latchAt) return out;
  for (const group of clusterByProximity(anchors, opts.radiusPx)) {
    if (group.length < opts.latchAt) continue;
    const stack = latchStack(group.map((i) => anchors[i]), opts);
    out.placed.push(...stack.placed);
    out.ellipses.push(...stack.ellipses);
    out.hiddenIds.push(...stack.hiddenIds);
  }
  return out;
}

// ============================================================================
// IDENTICAL-TEXT RADIUS DEDUPE — one string per 24px screen neighbourhood per frame
// ============================================================================
// THE BUGS THIS FIXES: (1) the stacked "WP-2 ×3" waypoint label (Will's screenshot
// 2026-07-06) — several label instances carrying the IDENTICAL text at the IDENTICAL
// screen anchor; (2) the visible ECHO TWIN (v2 §1.4, "COALITION-BRIEF ×2") — the
// same text at a SLIGHTLY DIFFERENT anchor, which the old exact-pixel slot key
// could not catch. Duplicates arise two independent ways:
//   (a) SPEC FAN-OUT: the engine routes an entity into EVERY layer spec whose
//       `kinds` match (engine.ts tick), and each spec's ::callsign TextLayer draws
//       its own copy (a kind:"track" lives in both the tracks and adsb specs).
//   (b) EGRESS ECHO: an authored marker that crossed /emit returns from the TAK
//       net as a SECOND entity (different id, same name, near-same point) — its
//       anchor drifts a few px, so it needs a RADIUS, not a pixel key.
// The fix is to DEDUPE at the source: the FIRST label to claim a (text @ 24px
// neighbourhood) slot draws; an identical string within LABEL_DEDUPE_RADIUS_PX of a
// prior claim does not. The winner is deterministic — the caller feeds labels in the
// (rank, id) sort order, so the first claimant (the local marker, not the echo) wins
// every rebuild. The REAL fix (gateway origin-id round-trip dedupe) stays booked;
// this is the render-side mitigation. PRESENTATION ONLY (I13): only a redundant
// string is skipped — BOTH entities keep their icons and stay pickable.

/** Same-text dedupe radius (px): an identical string within this distance of an already-
 *  claimed label does not draw. 24px ≈ one label line — far enough to catch the /emit echo
 *  twin's anchor drift, near enough that two REAL same-named tracks a visible distance
 *  apart both keep their labels (30px apart ⇒ both draw; T0-pinned). */
export const LABEL_DEDUPE_RADIUS_PX = 24;

/** Per-pass claim registry for the radius dedupe: every claimed label's screen position,
 *  grid-bucketed at radius-sized cells and keyed `${cellX},${cellY}|${text}` — so a claim
 *  probe compares only the 3×3 neighbourhood of same-text buckets, O(1) expected. The
 *  caller OWNS it and its lifetime: one per frame/rebuild pass, shared across every label
 *  surface in that pass, cleared between passes (a plain Map — `.clear()` works). */
export type LabelClaims = Map<string, Array<{ x: number; y: number }>>;

/** Claim the (text @ 24px-neighbourhood) slot for one label. Returns true when this label
 *  WINS (first claim — the caller draws it), false when an identical string already claimed
 *  a position within {@link LABEL_DEDUPE_RADIUS_PX} this frame (a redundant twin — the
 *  caller must not draw it). Cell size == radius, so any pair within the radius is at most
 *  one cell apart per axis — the 3×3 probe is exhaustive; a true DISTANCE check inside the
 *  probe keeps the boundary honest (24px dedupes, 30px does not — never bucket-fuzzy).
 *  Determinism is the caller's feed order (specs in registration order, labels in (rank, id)
 *  order ⇒ the same winner every rebuild — the stable-placement lesson). A blank text is
 *  not a label: it never claims and never collides. Different text NEVER dedupes — a real
 *  pile deconflicts, it does not vanish. PURE apart from the claim written into `claimed`. */
export function claimLabelSlot(claimed: LabelClaims, text: string, x: number, y: number): boolean {
  if (text === "") return true; // a blank is not a label — never claims, never collides
  const r = LABEL_DEDUPE_RADIUS_PX;
  const cx = Math.floor(x / r);
  const cy = Math.floor(y / r);
  const r2 = r * r;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const near = claimed.get(`${cx + dx},${cy + dy}|${text}`);
      if (!near) continue;
      for (const p of near) {
        const ddx = p.x - x;
        const ddy = p.y - y;
        if (ddx * ddx + ddy * ddy <= r2) return false; // an identical string owns this spot
      }
    }
  }
  const key = `${cx},${cy}|${text}`;
  let mine = claimed.get(key);
  if (!mine) claimed.set(key, (mine = []));
  mine.push({ x, y });
  return true;
}
